import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Zap, LogIn, UserPlus, ArrowLeft } from "lucide-react";

const LoginPage = () => {
    const { login, register, user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [loginData, setLoginData] = useState({ email: "", password: "" });
    const [registerData, setRegisterData] = useState({
        email: "",
        password: "",
        full_name: "",
        confirmPassword: ""
    });

    // Redirect if already logged in — done in effect so we don't call
    // navigate() during render (which triggers React's setState-in-render warning).
    useEffect(() => {
        if (user) {
            navigate(user.role === "user" ? "/" : "/admin", { replace: true });
        }
    }, [user, navigate]);

    if (user) return null;

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const userData = await login(loginData.email, loginData.password);
            toast.success("Umefanikiwa kuingia!");
            navigate(userData.role === "user" ? "/" : "/admin");
        } catch (error) {
            toast.error(error.response?.data?.detail || "Imeshindikana kuingia");
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (registerData.password !== registerData.confirmPassword) {
            toast.error("Nywila hazifanani");
            return;
        }
        setLoading(true);
        try {
            await register(
                registerData.email,
                registerData.password,
                registerData.full_name
            );
            toast.success("Akaunti imeundwa!");
            navigate("/");
        } catch (error) {
            toast.error(error.response?.data?.detail || "Imeshindikana kujiandikisha");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-obsidian flex flex-col">
            {/* Header */}
            <header className="container-main py-4">
                <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors">
                    <ArrowLeft className="h-4 w-4" />
                    Rudi Nyumbani
                </Link>
            </header>

            {/* Main Content */}
            <main className="flex-1 flex items-center justify-center px-4 py-8">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-flex items-center gap-2">
                            <Zap className="h-10 w-10 text-electric" />
                            <span className="dimbani-wordmark text-5xl text-white">
                                DIMBANI
                            </span>
                        </Link>
                        <p className="text-white/50 mt-2">Jukwaa la Michezo</p>
                    </div>

                    <Card className="bg-paper border-border" data-testid="auth-card">
                        <Tabs defaultValue="login" className="w-full">
                            <TabsList className="grid w-full grid-cols-2 bg-subtle">
                                <TabsTrigger 
                                    value="login" 
                                    className="data-[state=active]:bg-electric data-[state=active]:text-white font-heading uppercase"
                                    data-testid="login-tab"
                                >
                                    Ingia
                                </TabsTrigger>
                                <TabsTrigger 
                                    value="register" 
                                    className="data-[state=active]:bg-electric data-[state=active]:text-white font-heading uppercase"
                                    data-testid="register-tab"
                                >
                                    Jiandikishe
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="login">
                                <CardHeader>
                                    <CardTitle className="text-white font-heading uppercase">Karibu Tena</CardTitle>
                                    <CardDescription className="text-white/50">
                                        Ingiza maelezo yako kuendelea
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleLogin} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="login-email" className="text-white">Barua pepe</Label>
                                            <Input
                                                id="login-email"
                                                type="email"
                                                placeholder="jina@mfano.com"
                                                value={loginData.email}
                                                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                                                required
                                                className="bg-subtle border-border text-white placeholder:text-white/30"
                                                data-testid="login-email-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="login-password" className="text-white">Nywila</Label>
                                            <Input
                                                id="login-password"
                                                type="password"
                                                placeholder="••••••••"
                                                value={loginData.password}
                                                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                                                required
                                                className="bg-subtle border-border text-white placeholder:text-white/30"
                                                data-testid="login-password-input"
                                            />
                                        </div>
                                        <Button
                                            type="submit"
                                            className="w-full btn-primary"
                                            disabled={loading}
                                            data-testid="login-submit-btn"
                                        >
                                            {loading ? (
                                                <span className="animate-pulse">Inaendelea...</span>
                                            ) : (
                                                <>
                                                    <LogIn className="h-4 w-4 mr-2" />
                                                    Ingia
                                                </>
                                            )}
                                        </Button>
                                    </form>
                                </CardContent>
                            </TabsContent>

                            <TabsContent value="register">
                                <CardHeader>
                                    <CardTitle className="text-white font-heading uppercase">Unda Akaunti</CardTitle>
                                    <CardDescription className="text-white/50">
                                        Jiunge na Dimbani leo
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleRegister} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="register-name" className="text-white">Jina kamili</Label>
                                            <Input
                                                id="register-name"
                                                type="text"
                                                placeholder="Jina Ukoo"
                                                value={registerData.full_name}
                                                onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                                                required
                                                className="bg-subtle border-border text-white placeholder:text-white/30"
                                                data-testid="register-name-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="register-email" className="text-white">Barua pepe</Label>
                                            <Input
                                                id="register-email"
                                                type="email"
                                                placeholder="jina@mfano.com"
                                                value={registerData.email}
                                                onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                                                required
                                                className="bg-subtle border-border text-white placeholder:text-white/30"
                                                data-testid="register-email-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="register-password" className="text-white">Nywila</Label>
                                            <Input
                                                id="register-password"
                                                type="password"
                                                placeholder="••••••••"
                                                value={registerData.password}
                                                onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                                                required
                                                minLength={6}
                                                className="bg-subtle border-border text-white placeholder:text-white/30"
                                                data-testid="register-password-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="register-confirm" className="text-white">Thibitisha Nywila</Label>
                                            <Input
                                                id="register-confirm"
                                                type="password"
                                                placeholder="••••••••"
                                                value={registerData.confirmPassword}
                                                onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                                                required
                                                className="bg-subtle border-border text-white placeholder:text-white/30"
                                                data-testid="register-confirm-input"
                                            />
                                        </div>
                                        <Button
                                            type="submit"
                                            className="w-full btn-primary"
                                            disabled={loading}
                                            data-testid="register-submit-btn"
                                        >
                                            {loading ? (
                                                <span className="animate-pulse">Inaendelea...</span>
                                            ) : (
                                                <>
                                                    <UserPlus className="h-4 w-4 mr-2" />
                                                    Jiandikishe
                                                </>
                                            )}
                                        </Button>
                                    </form>
                                </CardContent>
                            </TabsContent>
                        </Tabs>
                    </Card>

                    {/* Demo Accounts */}
                    <div className="mt-6 p-4 bg-paper/50 border border-border rounded-md">
                        <p className="text-white/50 text-xs text-center mb-3">Akaunti za Majaribio (Demo)</p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="p-2 bg-subtle rounded">
                                <p className="text-electric font-mono">Admin</p>
                                <p className="text-white/40">admin@dimbani.com</p>
                            </div>
                            <div className="p-2 bg-subtle rounded">
                                <p className="text-solar font-mono">Content</p>
                                <p className="text-white/40">editor@dimbani.com</p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default LoginPage;
